import { BlockDefinition, BlockId, LegacyBlockTheme } from "@softr-io/blocks-sdk";
import { areBlockDefinitionsEqual } from "./areBlockDefinitionsEqual";
import { keyBy } from "lodash-es";
/**
 * In the studio, whenever any setting changes, the block definition and theme object
 * is mutated. This means we can never be sure with a shallow comparison if
 * we can skip the rendering of a block or not.
 * {@link BlockStabilizer} implements deep comparison of block definitions, which
 * preserves the reference of the block definition objects if they haven't changed.
 * If the block definition has changed, a deep clone is performed.
 * Until the studio codebase is refactored to not mutate the block definitions,
 * we need this stabilizer to avoid re-rendering every block on the page when any
 * setting changes.
 *
 * @example
 * const stabillizer = makeBlockStabilizer({ definitions: studioBlocks, theme });
 *
 * // in `update`
 * stabilizer.update({ definitions: newBlocks, theme: newTheme });
 * // returns the same reference for the block if it hasn't changed
 * stabilizer.currentState().definitions.find(it => it.id === "something");
 */
type BlockStabilizer = {
    update(args: BlockStabilizerArgs): BlockStabilizerState;
    currentState(): BlockStabilizerState;
};

type BlockStabilizerState = {
    definitions: readonly BlockDefinition[];
    theme: LegacyBlockTheme;
};
type BlockStabilizerArgs = {
    definitions: readonly BlockDefinition[];
    theme: LegacyBlockTheme;
};

export function makeBlockStabilizer(initial: BlockStabilizerArgs): BlockStabilizer {
    const initialDefinitionsClone: BlockDefinition[] = JSON.parse(JSON.stringify(initial.definitions));

    let definitionsById: Record<BlockId, BlockDefinition> = keyBy(initialDefinitionsClone, "id");
    let currentState: BlockStabilizerState = {
        definitions: initialDefinitionsClone,
        theme: JSON.parse(JSON.stringify(initial.theme)),
    };
    return {
        update(newArgs) {
            const newThemeString = JSON.stringify(newArgs.theme);
            const definitions = newArgs.definitions.map((newDef) => {
                if (areBlockDefinitionsEqual(newDef, definitionsById[newDef.id])) {
                    return definitionsById[newDef.id];
                } else {
                    return JSON.parse(JSON.stringify(newDef));
                }
            });
            definitionsById = keyBy(definitions, "id");
            currentState = {
                definitions,
                theme:
                    newThemeString === JSON.stringify(currentState.theme)
                        ? currentState.theme
                        : JSON.parse(newThemeString),
            };
            return currentState;
        },
        currentState() {
            return currentState;
        },
    };
}
